import React, { useState, useMemo } from 'react';
import { Modal, Spinner, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks';
import { hideTicketLifecycle } from '../../ReduxSlices/ticketLifecycleSlice';
import { useTicketLifecycleQuery } from '../../queries/ticketLifecycle.query';
import {
  LifecycleNode,
  StageState,
  ColorKey,
  formatMoney,
  formatDate,
  getRelativeTime,
  daysBetween,
  getEntryPoint,
  isCameraTicket,
  getTicketStatus,
  getPathTaken
} from '../../types/ticketLifecycle.types';
import './TicketLifecycle.css';

const TicketLifecycleJourney: React.FC = () => {
  const dispatch = useDispatch();
  const metadata = useAppSelector((state) => state.ticketLifecycle.metadata);
  const { data: ticket, isLoading, isError } = useTicketLifecycleQuery(metadata.ticketId);

  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [hoveredDateKey, setHoveredDateKey] = useState<string | null>(null);
  const [chargeHistoryOpen, setChargeHistoryOpen] = useState(false);

  const handleClose = () => {
    dispatch(hideTicketLifecycle());
    setSelectedStageId(null);
    setChargeHistoryOpen(false);
  };

  // Build lifecycle nodes from ticket data
  const lifecycleData = useMemo(() => {
    if (!ticket) return null;

    const now = new Date();
    const entryPoint = getEntryPoint(ticket.insertedBy);
    const pathTaken = getPathTaken(ticket);
    const status = getTicketStatus(ticket);

    const estimationDays = ticket.settled
      ? daysBetween(ticket.insertedAt, ticket.lastSyncedWithNYC || ticket.insertedAt)
      : daysBetween(ticket.insertedAt, now.toISOString());

    // Shared nodes: Ingestion and Estimation
    const ingestionState: StageState = 'done';
    const estimationState: StageState = ticket.settled ? 'done' : 'current';

    const ingestion: LifecycleNode = {
      id: 'ingestion',
      label: 'Ingestion',
      sublabel: formatDate(ticket.insertedAt) || '',
      state: ingestionState,
      dateLabel: 'Inserted At',
      date: formatDate(ticket.insertedAt),
      description: `Ticket entered the system via ${entryPoint}. Source: ${
        ticket.insertedBy || 'Unknown'
      }.`,
      colorKey: 'blue'
    };

    const estimation: LifecycleNode = {
      id: 'estimation',
      label: 'Estimation',
      sublabel: ticket.settled ? `${estimationDays || 0} days` : `day ${estimationDays || 0}`,
      state: estimationState,
      dateLabel: 'Last Synced',
      date: formatDate(ticket.lastSyncedWithNYC),
      description: ticket.estimateId
        ? `Ticket linked to Estimate, actively fought with NYC. ${
            ticket.settled
              ? `Resolved after ${estimationDays} days.`
              : `Ongoing for ${estimationDays} days, awaiting NYC decision.`
          }`
        : 'Ticket awaiting estimate creation.',
      colorKey: 'blue'
    };

    // Fork node
    const forkState: StageState = ticket.settled ? 'done' : 'future';
    const forkColorKey: ColorKey = ticket.settled
      ? ticket.isDismissed
        ? 'green'
        : 'amber'
      : 'gray';

    const fork: LifecycleNode = {
      id: 'fork',
      label: ticket.settled ? (ticket.isDismissed ? 'DISMISSED' : 'GUILTY') : 'Pending',
      sublabel: ticket.settled ? formatDate(ticket.lastSyncedWithNYC) || 'decision' : 'decision',
      state: forkState,
      dateLabel: 'Settled',
      date: ticket.settled ? formatDate(ticket.lastSyncedWithNYC) : null,
      description: ticket.settled
        ? `NYC settlement decision received. Outcome: ${
            ticket.isDismissed
              ? 'Dismissed - customer saved money.'
              : 'Guilty - customer owes NYC directly.'
          }`
        : 'Awaiting NYC settlement decision. This fork has not been reached yet.',
      colorKey: forkColorKey
    };

    // Dismissed track nodes
    let dismissedNodes: LifecycleNode[];
    if (pathTaken === 'dismissed') {
      const paidDone = ticket.chargeAttempts.some((ca) => ca.success && !ca.refundedAt);
      const chargeAttempt = ticket.chargeAttempts[0];

      dismissedNodes = [
        {
          id: 'd_invoice',
          label: 'Invoice Generated',
          sublabel: ticket.invoiceFreshbooksId ? `INV-${ticket.invoiceFreshbooksId}` : '\u2014',
          state: 'done',
          dateLabel: 'Invoice',
          date: ticket.invoiceFreshbooksId ? `INV-${ticket.invoiceFreshbooksId}` : null,
          description: `Invoice ${
            ticket.invoiceFreshbooksId || 'pending'
          } generated for 50% of savings fee.`,
          colorKey: 'green'
        },
        {
          id: 'd_charge',
          label: 'Charge Attempted',
          sublabel: chargeAttempt ? formatDate(chargeAttempt.attemptDate) || '' : '\u2014',
          state: paidDone ? 'done' : ticket.chargeAttempts.length > 0 ? 'done' : 'future',
          dateLabel: 'Attempt Date',
          date: chargeAttempt ? formatDate(chargeAttempt.attemptDate) : null,
          description: chargeAttempt
            ? `Charge attempt via ${chargeAttempt.source}.`
            : 'Awaiting charge attempt.',
          colorKey: paidDone ? 'green' : chargeAttempt ? 'red' : 'gray'
        },
        {
          id: 'd_paid',
          label: 'Paid',
          sublabel: paidDone ? 'confirmed' : 'pending',
          state: paidDone ? 'done' : 'future',
          dateLabel: 'Status',
          date: paidDone ? 'Paid' : null,
          description: paidDone
            ? 'Payment confirmed. Status set to Paid.'
            : 'Charge failed - status remains Invoices (pending), may retry via Charge All.',
          colorKey: paidDone ? 'green' : 'gray'
        }
      ];
    } else {
      dismissedNodes = [
        {
          id: 'd_stub',
          label: 'Dismissed Path',
          sublabel: 'not taken',
          state: 'skipped',
          dateLabel: null,
          date: null,
          description: 'This ticket did not take the Dismissed path.',
          colorKey: 'gray'
        }
      ];
    }

    // Guilty track nodes
    let guiltyNodes: LifecycleNode[];
    if (pathTaken === 'guilty') {
      if (ticket.autoPayToNYC) {
        const chargeOk = ticket.chargeAttempts.some((ca) => ca.success && !ca.refundedAt);
        const chargeAttempt = ticket.chargeAttempts[0];

        guiltyNodes = [
          {
            id: 'g_auto',
            label: 'Auto-Charge Attempt',
            sublabel: chargeAttempt ? formatDate(chargeAttempt.attemptDate) || '' : '\u2014',
            state: 'done',
            dateLabel: 'Attempt Date',
            date: chargeAttempt ? formatDate(chargeAttempt.attemptDate) : null,
            description: 'AutoPayToNYC enabled - $5 auto-charge attempted at invoice generation.',
            colorKey: chargeOk ? 'green' : 'red'
          },
          {
            id: 'g_result',
            label: chargeOk ? 'Paid' : 'Delinquent',
            sublabel: chargeOk ? 'invoice paid' : formatDate(ticket.sentToCustomerAt) || '',
            state: 'done',
            dateLabel: chargeOk ? 'Status' : 'Notified',
            date: chargeOk ? 'Paid' : formatDate(ticket.sentToCustomerAt),
            description: chargeOk
              ? 'Invoice created and paid immediately.'
              : 'Charge failed - no invoice created, email sent to customer.',
            colorKey: chargeOk ? 'green' : 'amber'
          }
        ];
      } else {
        guiltyNodes = [
          {
            id: 'g_notify',
            label: 'Customer Notified',
            sublabel: formatDate(ticket.sentToCustomerAt) || '\u2014',
            state: ticket.sentToCustomerAt ? 'done' : 'future',
            dateLabel: 'Sent To Customer',
            date: formatDate(ticket.sentToCustomerAt),
            description:
              'AutoPayToNYC disabled - no invoice created. Email sent notifying customer to pay NYC directly.',
            colorKey: 'amber'
          },
          {
            id: 'g_resolve',
            label: ticket.paidToNycAt ? 'Paid' : 'Delinquent',
            sublabel: ticket.paidToNycAt
              ? formatDate(ticket.paidToNycAt) || ''
              : 'awaiting payment',
            state: ticket.paidToNycAt ? 'done' : 'current',
            dateLabel: ticket.paidToNycAt ? 'Paid To NYC' : 'Status',
            date: ticket.paidToNycAt ? formatDate(ticket.paidToNycAt) : 'Delinquent',
            description: ticket.paidToNycAt
              ? 'NYC API sync confirmed payment received directly from customer.'
              : 'Waiting for customer to pay NYC or for NYC sync confirmation.',
            colorKey: ticket.paidToNycAt ? 'green' : 'red'
          }
        ];
      }
    } else {
      guiltyNodes = [
        {
          id: 'g_stub',
          label: 'Guilty Path',
          sublabel: 'not taken',
          state: 'skipped',
          dateLabel: null,
          date: null,
          description: 'This ticket did not take the Guilty path.',
          colorKey: 'gray'
        }
      ];
    }

    // Date fields
    const dateFields = [
      { key: 'insertedAt', label: 'Inserted At', value: ticket.insertedAt },
      { key: 'lastSyncedWithNYC', label: 'Last Synced w/ NYC', value: ticket.lastSyncedWithNYC },
      {
        key: 'lastAmountsSyncedAt',
        label: 'Last Amounts Synced',
        value: ticket.lastAmountsSyncedAt
      },
      { key: 'printedAt', label: 'Printed At', value: ticket.printedAt },
      { key: 'sentToCustomerAt', label: 'Sent To Customer', value: ticket.sentToCustomerAt },
      { key: 'paidToNycAt', label: 'Paid To NYC', value: ticket.paidToNycAt },
      { key: 'judgmentEntryDate', label: 'Judgment Entry', value: ticket.judgmentEntryDate }
    ].filter((d) => d.value);

    // Badges
    const badges: Array<{ label: string; className: string }> = [];
    if (ticket.ignoreTicket) badges.push({ label: 'Ignored', className: 'ignored' });
    if (ticket.isPaidOutsideSystem)
      badges.push({ label: 'Paid Outside System', className: 'paid-outside' });
    if (ticket.judgmentEntryDate) badges.push({ label: 'Towing Risk', className: 'towing-risk' });
    if (isCameraTicket(ticket.summonsNumber))
      badges.push({ label: 'Camera Ticket', className: 'camera' });

    return {
      ticket,
      entryPoint,
      pathTaken,
      status,
      sharedNodes: [ingestion, estimation],
      fork,
      dismissedNodes,
      guiltyNodes,
      dateFields,
      badges,
      allNodes: [ingestion, estimation, fork, ...dismissedNodes, ...guiltyNodes]
    };
  }, [ticket]);

  // Get selected node for detail panel
  const selectedNode = useMemo(() => {
    if (!lifecycleData) return null;

    let nodeId = selectedStageId;
    if (!nodeId) {
      // Default to current or last done node
      const currentNode = lifecycleData.allNodes.find((n) => n.state === 'current');
      if (currentNode) {
        nodeId = currentNode.id;
      } else {
        const doneNodes = lifecycleData.allNodes.filter((n) => n.state === 'done');
        nodeId = doneNodes.length > 0 ? doneNodes[doneNodes.length - 1].id : 'ingestion';
      }
    }

    return lifecycleData.allNodes.find((n) => n.id === nodeId) || lifecycleData.sharedNodes[0];
  }, [lifecycleData, selectedStageId]);

  const getNodeIcon = (state: StageState): string => {
    switch (state) {
      case 'done':
        return '\u2713'; // checkmark
      case 'current':
        return '\u25CF'; // filled circle
      case 'skipped':
        return '\u2715'; // x
      default:
        return '\u00B7'; // middle dot
    }
  };

  const getStateLabel = (state: StageState): string => {
    switch (state) {
      case 'done':
        return 'Completed';
      case 'current':
        return 'In Progress';
      case 'future':
        return 'Not Reached';
      case 'skipped':
        return 'Not Taken';
      default:
        return state;
    }
  };

  const getStateClassName = (state: StageState): string => {
    switch (state) {
      case 'done':
        return 'completed';
      case 'current':
        return 'in-progress';
      case 'future':
        return 'not-reached';
      case 'skipped':
        return 'not-taken';
      default:
        return '';
    }
  };

  const getStatusClassName = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'paid';
      case 'delinquent':
        return 'delinquent';
      case 'estimates':
        return 'estimates';
      case 'invoices':
      case 'waitingtobeinvoiced':
        return 'invoices';
      default:
        return 'estimates';
    }
  };

  const renderNode = (node: LifecycleNode, isSmall = false) => (
    <div
      key={node.id}
      className={`lifecycle-node ${node.state} ${node.colorKey} ${isSmall ? 'small' : ''}`}
      onClick={() => setSelectedStageId(node.id)}>
      <div className="lifecycle-node-header">
        <span className="lifecycle-node-icon">{getNodeIcon(node.state)}</span>
        <span className="lifecycle-node-label">{node.label}</span>
      </div>
      <div className="lifecycle-node-sublabel">{node.sublabel}</div>
    </div>
  );

  if (!metadata.showModal) return null;

  return (
    <Modal
      show={metadata.showModal}
      onHide={handleClose}
      size="xl"
      centered
      className="lifecycle-modal"
      dialogClassName="modal-90w">
      <Modal.Body>
        {isLoading && (
          <div className="lifecycle-loading">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {isError && (
          <div className="lifecycle-error">
            <p>Failed to load ticket lifecycle data.</p>
            <Button variant="outline-secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        )}

        {lifecycleData && ticket && (
          <>
            {/* Header */}
            <div className="lifecycle-header">
              <div>
                <div className="lifecycle-header-title">
                  Summons {ticket.summonsNumber}
                  <span className="lifecycle-header-plate">{ticket.plateNumber}</span>
                </div>
                <div className="lifecycle-header-subtitle">
                  Entry: {lifecycleData.entryPoint} &middot; inserted{' '}
                  {formatDate(ticket.insertedAt)}
                </div>
              </div>

              <span
                className={`lifecycle-status-badge ${getStatusClassName(lifecycleData.status)}`}>
                {lifecycleData.status}
              </span>

              {lifecycleData.badges.map((badge) => (
                <span key={badge.className} className={`lifecycle-badge ${badge.className}`}>
                  {badge.label}
                </span>
              ))}

              <div style={{ flex: 1 }} />

              <Button variant="outline-secondary" size="sm" onClick={handleClose}>
                Close
              </Button>
            </div>

            {/* Flowchart */}
            <div className="lifecycle-flowchart">
              <div className="lifecycle-flowchart-inner">
                {/* Shared nodes */}
                {lifecycleData.sharedNodes.map((node, i) => (
                  <React.Fragment key={node.id}>
                    {i > 0 && <span className="lifecycle-arrow">&rarr;</span>}
                    {renderNode(node)}
                  </React.Fragment>
                ))}

                <span className="lifecycle-arrow">&rarr;</span>

                {/* Fork */}
                <div
                  className={`lifecycle-fork ${lifecycleData.fork.state} ${lifecycleData.fork.colorKey}`}
                  onClick={() => setSelectedStageId('fork')}>
                  <div className="lifecycle-fork-content">
                    <div className="lifecycle-fork-label">{lifecycleData.fork.label}</div>
                    <div className="lifecycle-fork-sublabel">{lifecycleData.fork.sublabel}</div>
                  </div>
                </div>

                <span className="lifecycle-arrow">&rarr;</span>

                {/* Branches */}
                <div className="lifecycle-branches">
                  {/* Dismissed track */}
                  <div className="lifecycle-branch">
                    {lifecycleData.dismissedNodes.map((node, i) => (
                      <React.Fragment key={node.id}>
                        {i > 0 && <span className="lifecycle-arrow">&rarr;</span>}
                        {renderNode(node, true)}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Guilty track */}
                  <div className="lifecycle-branch">
                    {lifecycleData.guiltyNodes.map((node, i) => (
                      <React.Fragment key={node.id}>
                        {i > 0 && <span className="lifecycle-arrow">&rarr;</span>}
                        {renderNode(node, true)}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Lower Panels */}
            <div className="lifecycle-panels">
              {/* Data Panel */}
              <div className="lifecycle-data-panel">
                <div className="lifecycle-panel-title">Ticket Data</div>

                <div className="lifecycle-amounts-grid">
                  <div className="lifecycle-amount-card">
                    <div className="lifecycle-amount-label">Fine Amount</div>
                    <div className="lifecycle-amount-value">{formatMoney(ticket.fineAmount)}</div>
                  </div>
                  <div className="lifecycle-amount-card">
                    <div className="lifecycle-amount-label">Reduction</div>
                    <div className="lifecycle-amount-value">
                      {formatMoney(ticket.reductionAmount)}
                    </div>
                  </div>
                  <div className="lifecycle-amount-card">
                    <div className="lifecycle-amount-label">Amount Due</div>
                    <div
                      className={`lifecycle-amount-value ${
                        ticket.amountDue && ticket.amountDue > 0 ? 'danger' : ''
                      }`}>
                      {formatMoney(ticket.amountDue)}
                    </div>
                  </div>
                </div>

                <div className="lifecycle-links">
                  {ticket.estimateFreshbooksId && (
                    <button className="lifecycle-link">
                      Estimate {ticket.estimateFreshbooksId}
                    </button>
                  )}
                  {ticket.invoiceFreshbooksId && (
                    <button className="lifecycle-link">Invoice {ticket.invoiceFreshbooksId}</button>
                  )}
                </div>

                <div className="lifecycle-timestamps-title">Timestamps</div>
                <div className="lifecycle-timestamps">
                  {lifecycleData.dateFields.map((field) => (
                    <div
                      key={field.key}
                      className="lifecycle-timestamp-row"
                      onMouseEnter={() => setHoveredDateKey(field.key)}
                      onMouseLeave={() => setHoveredDateKey(null)}>
                      <span className="lifecycle-timestamp-label">{field.label}</span>
                      <span className="lifecycle-timestamp-value">
                        {hoveredDateKey === field.key
                          ? getRelativeTime(field.value)
                          : formatDate(field.value)}
                      </span>
                      {hoveredDateKey === field.key && (
                        <div className="lifecycle-timestamp-tooltip">
                          {getRelativeTime(field.value)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Stage Detail Panel */}
              <div className="lifecycle-detail-panel">
                <div className="lifecycle-panel-title">Stage Detail</div>

                {selectedNode && (
                  <div className="lifecycle-detail-card">
                    <div className="lifecycle-detail-header">
                      <span
                        className="lifecycle-detail-icon"
                        style={{
                          backgroundColor:
                            selectedNode.state === 'done' || selectedNode.state === 'current'
                              ? selectedNode.colorKey === 'green'
                                ? '#10b981'
                                : selectedNode.colorKey === 'blue'
                                ? '#3b82f6'
                                : selectedNode.colorKey === 'amber'
                                ? '#f59e0b'
                                : selectedNode.colorKey === 'red'
                                ? '#ef4444'
                                : '#d1d5db'
                              : '#d1d5db',
                          color:
                            selectedNode.state === 'done' || selectedNode.state === 'current'
                              ? '#fff'
                              : '#9ca3af'
                        }}>
                        {getNodeIcon(selectedNode.state)}
                      </span>
                      <span className="lifecycle-detail-label">{selectedNode.label}</span>
                      <span
                        className={`lifecycle-detail-state ${getStateClassName(
                          selectedNode.state
                        )}`}>
                        {getStateLabel(selectedNode.state)}
                      </span>
                    </div>
                    <div className="lifecycle-detail-description">{selectedNode.description}</div>
                    {selectedNode.date && (
                      <div className="lifecycle-detail-date">
                        {selectedNode.dateLabel}: {selectedNode.date}
                      </div>
                    )}
                  </div>
                )}

                {/* Charge Attempt History */}
                {ticket.chargeAttempts.length > 0 && (
                  <>
                    <div
                      className="lifecycle-charge-toggle"
                      onClick={() => setChargeHistoryOpen(!chargeHistoryOpen)}>
                      <span className="lifecycle-charge-toggle-label">
                        Charge Attempt History ({ticket.chargeAttempts.length})
                      </span>
                      <span className="lifecycle-charge-toggle-icon">
                        {chargeHistoryOpen ? '\u25B2' : '\u25BC'}
                      </span>
                    </div>

                    {chargeHistoryOpen && (
                      <div className="lifecycle-charge-list">
                        {ticket.chargeAttempts.map((attempt) => (
                          <div key={attempt.id} className="lifecycle-charge-item">
                            <span
                              className={`lifecycle-charge-dot ${
                                attempt.success ? 'success' : 'failed'
                              }`}
                            />
                            <span className="lifecycle-charge-date">
                              {formatDate(attempt.attemptDate)}
                            </span>
                            <span className="lifecycle-charge-source">{attempt.source}</span>
                            <span className="lifecycle-charge-amount">
                              {formatMoney(attempt.amount)}
                            </span>
                            <span
                              className={`lifecycle-charge-status ${
                                attempt.refundedAt
                                  ? 'refunded'
                                  : attempt.success
                                  ? 'success'
                                  : 'failed'
                              }`}>
                              {attempt.refundedAt
                                ? 'Refunded'
                                : attempt.success
                                ? 'Success'
                                : 'Failed'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TicketLifecycleJourney;
